/**
 * Change Password Form Component
 *
 * Form for changing user password with validation.
 * Supports both regular password change and reset token flow.
 * Uses React Hook Form with Yup validation and MUI components.
 * Integrates with RTK Query authApi for changePassword mutation.
 */

import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useChangePasswordMutation } from '../api/authApi';
import {
  changePasswordValidationSchema,
  ChangePasswordFormInputs,
} from '../validation/changePasswordValidationSchema';
import type { ChangePasswordRequest } from '../types/auth.types';

interface ChangePasswordFormProps {
  /**
   * Callback invoked after successful password change
   */
  onSuccess: () => void;

  /**
   * Optional reset token for password reset flow
   * If provided, allows password change without current password verification
   */
  resetToken?: string | null;
}

/**
 * ChangePasswordForm - Password change form component
 *
 * Features:
 * - Three input fields: current password, new password, confirm password
 * - Password visibility toggle for each field
 * - Real-time validation with Yup schema
 * - Backend error display
 * - Loading state during mutation
 * - Optional reset token support
 */
const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onSuccess, resetToken }) => {
  const [changePassword, { isLoading, error }] = useChangePasswordMutation();
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordFormInputs>({
    resolver: yupResolver(changePasswordValidationSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  /**
   * Toggle password visibility for specific field
   */
  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  /**
   * Handle form submission
   * Sends change password request to backend
   */
  const onSubmit = async (data: ChangePasswordFormInputs) => {
    try {
      const payload: ChangePasswordRequest = {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        resetToken: resetToken ?? null,
      };

      await changePassword(payload).unwrap();
      onSuccess();
    } catch (err) {
      // Error handled by RTK Query and displayed below
      console.error('Password change failed:', err);
    }
  };

  /**
   * Extract error message from RTK Query error
   */
  const errorMessage = error
    ? 'data' in error && typeof error.data === 'object' && error.data && 'message' in error.data
      ? (error.data as any).message
      : 'Błąd podczas zmiany hasła. Spróbuj ponownie.'
    : null;

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ maxWidth: 500, width: '100%' }}>
      {/* Header */}
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Zmiana hasła wymagana
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Ze względów bezpieczeństwa musisz zmienić hasło przed kontynuowaniem pracy w systemie.
      </Typography>

      {/* Error Alert */}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Current Password Field */}
      <Controller
        name="currentPassword"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Aktualne hasło"
            type={showPasswords.current ? 'text' : 'password'}
            fullWidth
            margin="normal"
            error={!!errors.currentPassword}
            helperText={errors.currentPassword?.message}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('current')}
                    edge="end"
                    disabled={isLoading}
                    aria-label="toggle current password visibility"
                  >
                    {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* New Password Field */}
      <Controller
        name="newPassword"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Nowe hasło"
            type={showPasswords.new ? 'text' : 'password'}
            fullWidth
            margin="normal"
            error={!!errors.newPassword}
            helperText={errors.newPassword?.message}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('new')}
                    edge="end"
                    disabled={isLoading}
                    aria-label="toggle new password visibility"
                  >
                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* Confirm Password Field */}
      <Controller
        name="confirmPassword"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Potwierdź nowe hasło"
            type={showPasswords.confirm ? 'text' : 'password'}
            fullWidth
            margin="normal"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('confirm')}
                    edge="end"
                    disabled={isLoading}
                    aria-label="toggle confirm password visibility"
                  >
                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {/* Submit Button */}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={isLoading}
        sx={{ mt: 3, py: 1.5 }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
            Zmiana hasła...
          </>
        ) : (
          'Zmień hasło'
        )}
      </Button>
    </Box>
  );
};

export default ChangePasswordForm;
